import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Cookie, X } from "lucide-react";

const KEY = "diplomexa_cookie_consent_v1";

/**
 * Sprint 4 — Public-site cookie banner.
 *
 * Persists the user's choice in localStorage AND in the backend's
 * `cookie_consents` collection (with policy version, IP and UA captured
 * server-side).
 */
export default function CookieConsentBanner() {
  const [open, setOpen] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [prefs, setPrefs] = useState({
    preferences: false, analytics: false, marketing: false,
  });

  useEffect(() => {
    try {
      if (localStorage.getItem(KEY)) return; // user already chose
      // If authenticated, we treat session-cookie acceptance as implicit
      // for essential cookies and skip the banner.
      if (localStorage.getItem("factskoot_token")) {
        localStorage.setItem(KEY, JSON.stringify({
          preferences: false, analytics: false, marketing: false,
          implicit: true,
        }));
        return;
      }
      setOpen(true);
    } catch { setOpen(true); }
  }, []);

  const save = async (choice) => {
    try { localStorage.setItem(KEY, JSON.stringify(choice)); } catch { /* noop */ }
    try { await api.post("/policies/cookie-consent", choice); } catch { /* noop */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-50 rounded-2xl border border-[#D4AF37]/40 bg-[#0A192F] text-white shadow-2xl p-5"
      data-testid="cookie-banner"
    >
      <div className="flex items-start gap-3">
        <Cookie className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm">
          <p className="font-semibold">We use cookies to run Diplomexa.</p>
          <p className="mt-1 text-xs text-slate-300">
            Essential cookies keep you signed in. Preference, analytics and
            marketing cookies are optional. See our{" "}
            <Link to="/cookie-policy" className="text-[#D4AF37] underline">
              Cookie Policy
            </Link>.
          </p>

          {showManage && (
            <div className="mt-3 space-y-1.5 text-xs">
              <CheckLine k="preferences" prefs={prefs} setPrefs={setPrefs} label="Preferences" />
              <CheckLine k="analytics" prefs={prefs} setPrefs={setPrefs} label="Analytics" />
              <CheckLine k="marketing" prefs={prefs} setPrefs={setPrefs} label="Marketing" />
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button"
                    onClick={() => save({ preferences: true, analytics: true, marketing: true })}
                    className="px-3 py-1.5 rounded-md bg-[#D4AF37] text-[#0A192F] text-xs font-bold"
                    data-testid="cookie-accept-all">Accept all</button>
            <button type="button"
                    onClick={() => save({ preferences: false, analytics: false, marketing: false })}
                    className="px-3 py-1.5 rounded-md border border-white/20 text-white text-xs"
                    data-testid="cookie-reject">Reject non-essential</button>
            <button type="button"
                    onClick={() => showManage ? save(prefs) : setShowManage(true)}
                    className="px-3 py-1.5 rounded-md border border-[#D4AF37]/40 text-[#D4AF37] text-xs"
                    data-testid="cookie-manage">
              {showManage ? "Save preferences" : "Manage"}
            </button>
          </div>
        </div>
        <button type="button" onClick={() => setOpen(false)}
                className="text-slate-400 hover:text-white" aria-label="Dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function CheckLine({ k, prefs, setPrefs, label }) {
  return (
    <label className="flex items-center gap-2 text-slate-300">
      <input type="checkbox" checked={!!prefs[k]} onChange={(e) => setPrefs({ ...prefs, [k]: e.target.checked })}
             data-testid={`cookie-pref-${k}`} />
      {label}
    </label>
  );
}
